/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useRef, useState } from 'react'
import type { AccordionImperativeHandle } from '@components/Accordion/interface'

import { useStoryReader } from '@hooks/book/useStoryReader'
import { useAuthentication } from '@hooks/useAuthentication'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { SortEnum } from '@interfaces/SortEnum'
import { BookEnum } from '@interfaces/BookEnum'
import { ChapterManageProps, ChapterManageAcceptProps } from './interface'
import { BookCategoryType } from '@models/book/BookCategoryType'
import { gtagBeginCheckout } from '@lib/gtag'
import { fbqBeginCheckout } from '@lib/facebookPixel'
import { ttqBeginCheckout } from '@lib/tiktok'
import { ChapterReaderType } from '@models/chapter/ChapterReaderType'
import { ChapterStatusEnum } from '@interfaces/ChapterStatusEnum'

export function withChapterManage(Component: React.FC<ChapterManageProps>) {
  function WithChapterManage(props: ChapterManageAcceptProps) {
    const { user, isAuthenticated, userCoin } = useAuthentication()
    const {
      bookId,
      chapters,
      data,
      isOwner,
      canAction,
      bookReadPromotionDetail,
      chapterGroups,
      isBuyAllChapterFetching,
      totalPrice,
    } = useStoryReader()

    const accordionRefs = useRef<AccordionImperativeHandle[] | null>([])
    const [toggleBuyChapters, setToggleBuyChapters] = useState(false)
    const [selectedPage, setSelectedPage] = useState<number[]>([])
    const [allChapters, setAllChapters] = useState<
      Record<number, ChapterReaderType[]>
    >({})
    const [selectedChapters, setSelectedChapters] = useState<
      ChapterReaderType[]
    >([])
    const [sorter, setSorter] = useState<SortEnum>(
      (localStorage.getItem('sortChapter') as SortEnum) || SortEnum.ASC
    )
    const buyAllChapterModal = useModal({ modal: 'buyChapterList' }, true)
    const notEnoughCoinModal = useModal({
      modal: 'notEnoughCoin',
      modalProps: {
        describeText: 'เนื้อเรื่องที่คุณเลือกอ่านติดเหรียญอยู่',
        purposeText: 'เข้าถึงเนื้อหานี้',
      },
    })
    const changeEmailModal = useModal({ modal: 'changeEmail' }, true)
    const loginModal = useModal({
      modal: 'login',
    })

    const allSelectedAllPage = useMemo(() => {
      let results: number[] = []
      Object.entries(allChapters).forEach(([page, value]) => {
        if (!value.length) return
        const allSelected = value.every(
          chapter => selectedChapters.findIndex(c => c.id === chapter.id) !== -1
        )
        if (allSelected) {
          results.push(Number(page))
        }
      })

      return results
    }, [allChapters, selectedChapters])

    const noBuyableChapterPage = useMemo(() => {
      let results: number[] = []
      Object.entries(allChapters).forEach(([page, value]) => {
        if (!value.length) {
          results.push(Number(page))
        }
      })
      return results
    }, [allChapters])

    const handleSortData = () => {
      const sorterType = sorter === SortEnum.ASC ? SortEnum.DESC : SortEnum.ASC
      localStorage.setItem('sortChapter', sorterType)
      setSorter(sorterType)
    }

    function buyAllChapter() {
      let summaryCurrentCoin = 0
      if (userCoin.paidCoin) {
        summaryCurrentCoin += userCoin.paidCoin.value
      }

      if (userCoin.freeCoin) {
        summaryCurrentCoin += Math.floor(userCoin.freeCoin.value)
      }

      if (userCoin.thirdCoin) {
        summaryCurrentCoin += Math.floor(userCoin.thirdCoin.value)
      }

      const _chapters = toggleBuyChapters ? selectedChapters : chapters
      const _totalPrice = toggleBuyChapters
        ? _chapters.reduce((acc, chapter) => {
            return acc + chapter.price
          }, 0)
        : totalPrice

      if (summaryCurrentCoin >= _totalPrice) {
        const bookCategory = data?.category ?? new BookCategoryType()
        const bookType = data?.bookType || BookEnum.NOVEL

        buyAllChapterModal.show({
          bookId,
          bookName: data?.title || '',
          bookCategory,
          withBuyChapterList: !toggleBuyChapters,
          chapters: _chapters,
          bookType,
          onBuyingSuccess: toggleBuyChapters ? handleBuyChapters : undefined,
        })

        const analyticPayload = {
          value: _totalPrice,
          currency: 'THB',
          items: _chapters.map(chapter => ({
            item_id: chapter.id,
            item_category: bookCategory.id,
            item_variant: bookType,
            item_name: chapter.chapterName,
            price: chapter.price,
          })),
        }
        gtagBeginCheckout(analyticPayload)
        fbqBeginCheckout(analyticPayload)
        ttqBeginCheckout(analyticPayload)
      } else {
        notEnoughCoinModal.show()
      }
    }

    function handleBuyAllChapter() {
      if (!canAction || isBuyAllChapterFetching) return

      if (!isAuthenticated) {
        loginModal.show()
      } else if (user.emailEditable) {
        changeEmailModal.show({
          onSuccess: buyAllChapter,
        })
      } else {
        buyAllChapter()
      }
    }

    const handleBuyChapters = useCallback(() => {
      if (!canAction || isBuyAllChapterFetching) {
        setToggleBuyChapters(false)
        setSelectedChapters([])
        setSelectedPage([])
        return
      }

      if (!isAuthenticated) {
        loginModal.show()
        setToggleBuyChapters(false)
        setSelectedChapters([])
        setSelectedPage([])
        return
      }

      if (user.emailEditable) {
        changeEmailModal.show({
          onSuccess: buyAllChapter,
        })
        setToggleBuyChapters(false)
        setSelectedChapters([])
        setSelectedPage([])
        return
      }

      const prevState = toggleBuyChapters
      setToggleBuyChapters(!prevState)

      if (prevState) {
        setSelectedChapters([])
        setSelectedPage([])
      }
    }, [
      toggleBuyChapters,
      canAction,
      isBuyAllChapterFetching,
      isAuthenticated,
      user,
    ])

    const handleSelectChapter = useCallback((chapter: ChapterReaderType) => {
      setSelectedChapters(prev => {
        const isExist = prev?.findIndex(c => c.id === chapter.id) !== -1
        if (isExist) {
          return prev?.filter(c => c.id !== chapter.id)
        }

        return [...prev, chapter]
      })
    }, [])

    const onAccordionChange = useCallback(
      (index: number, page: number, isChecked: boolean) => {
        const chaptersInPage = allChapters?.[page] ?? []
        if (isChecked) {
          accordionRefs.current?.[index]?.toggleAccordion(true)
          if (chaptersInPage.length) {
            setSelectedChapters(prev => {
              const newChapters = chaptersInPage.filter(
                chapter => !prev.find(c => c.id === chapter.id)
              )
              return [...prev, ...newChapters]
            })
            return
          }
          setSelectedPage(prev =>
            !prev.includes(page) ? [...prev, page] : prev
          )
        } else {
          setSelectedPage(prev => prev.filter(p => p !== page))
          setSelectedChapters(prev =>
            prev.filter(c => !chaptersInPage.find(ch => ch.id === c.id))
          )
        }
      },
      [accordionRefs, allChapters]
    )

    const handleSelectChapters = useCallback(
      (chapters: ChapterReaderType[], page: number) => {
        if (
          !canAction ||
          data?.penName.user.id === user.id ||
          !isAuthenticated ||
          user.isAdmin ||
          user.emailEditable
        ) {
          setAllChapters(c => ({
            ...c,
            [page]: [],
          }))
          return
        }

        const filteredChapters = chapters.filter(c => {
          // filter only buyable chapter
          const isRestricted = c.status === ChapterStatusEnum.RESTRICTED
          return !isRestricted && c.price > 0 && !c.isPaid
        })

        setAllChapters(c => ({
          ...c,
          [page]: filteredChapters,
        }))

        if (selectedPage.includes(page)) {
          setSelectedChapters(prev => {
            const newChapters = filteredChapters.filter(
              chapter => !prev.find(c => c.id === chapter.id)
            )
            return [...prev, ...newChapters]
          })
          setSelectedPage(prev => prev.filter(p => p !== page))
        }
      },
      [canAction, isAuthenticated, data, user, selectedPage]
    )

    const newProps = {
      bookId,
      sorter,
      chapterGroups,
      bookCategory: data?.category ?? new BookCategoryType(),
      totalChapter: data?.publishedChapterCount || 0,
      totalPrice,
      bookName: data?.title || '',
      showBuyAllChapterButton:
        !!totalPrice && !isOwner && !user.isAdmin && !toggleBuyChapters,
      isManga: data?.bookType === BookEnum.MANGA,
      canAction: canAction || isOwner || user.isAdmin,
      bookType: data?.bookType || BookEnum.NOVEL,
      isOwner: data?.penName.user.id === user.id,
      bookReadPromotionDetail,
      isBuyAllChapterFetching,
      handleSortData,
      handleBuyAllChapter,
      handleBuyChapters,
      toggleBuyChapters,
      selectedChapters,
      handleSelectChapter,
      handleSelectChapters,
      onAccordionChange,
      accordionRefs,
      allSelectedAllPage,
      noBuyableChapterPage,
      ...props,
    }

    return <Component {...newProps} />
  }

  return WithChapterManage
}
